import { useState } from 'react';

const STORAGE_KEY = 'user_settings_extraDisplayPrecision';
const DEFAULT_PRECISION = 0;
const MIN_PRECISION = 0;
const MAX_PRECISION = 5;

/**
 * Custom hook for persisting Extra Display Precision setting in localStorage.
 * This setting is global and applies to all sessions for the current user.
 */
export const useExtraDisplayPrecision = (): [number, (value: number) => void] => {
  // Initialize state from localStorage or use default
  const [extraPrecision, setExtraPrecision] = useState<number>(() => {
    if (typeof window === 'undefined') return DEFAULT_PRECISION;

    try {
      const savedValue = localStorage.getItem(STORAGE_KEY);
      if (savedValue !== null) {
        const parsed = parseInt(savedValue, 10);
        if (!isNaN(parsed) && parsed >= MIN_PRECISION && parsed <= MAX_PRECISION) {
          return parsed;
        }
      }
    } catch (error) {
      console.warn('Failed to load extra display precision setting:', error);
    }
    return DEFAULT_PRECISION;
  });

  // Custom setter that validates and saves to localStorage
  const setPersistentExtraPrecision = (value: number) => {
    // Validate the value
    const clampedValue = Math.min(MAX_PRECISION, Math.max(MIN_PRECISION, value));

    setExtraPrecision(clampedValue);

    try {
      localStorage.setItem(STORAGE_KEY, clampedValue.toString());
    } catch (error) {
      console.warn('Failed to save extra display precision setting:', error);
    }
  };

  return [extraPrecision, setPersistentExtraPrecision];
};
